import {
	Button,
	MenuDivider,
	MenuGroupHeader,
	MenuItemCheckbox,
	mergeClasses,
	Radio,
	RadioGroup,
	Divider,
	Text,
	MenuList,
	PresenceBadge,
} from "@fluentui/react-components";
import { useCallback, useState, useEffect } from "react";
import { useIntl } from "react-intl";
import { useInteractionMenuStyle } from "@/components/molecules/interactionMenu/style";
import { AppCacheActions, CacheKey, useAppCacheStore } from "@/common/stores/persistingStores/app";
import {
	SupervisorAgentsFilterActions,
	useSupervisorAgentsFilterStore,
	SupervisorAgentsFilterState,
} from "@/common/stores/nonpersistingStores/supervisorAgentsFilter";
import { Flex } from "@/components/atoms/flex";
import { useStyle } from "./style";
import { Availabilities } from "@/components/organisms/currentUserPresence/types";
import { mapPresenceToStatus } from "@/common/mappers/presenceMapper";
import { translateActivity } from "@/common/helpers/presenceHelper";
import type { MenuProps } from "@fluentui/react-components";
import { Circle16Filled } from "@fluentui/react-icons";
import {
	desireTypes,
	desireColors,
	desireName,
	initialSupervisorFilterAvailabilityList,
	castStringToBoolean,
	getInitialSupervisorFilterData,
} from "@/common/helpers/supervisorHelper";

export const SupervisorAgentFiltersPopoverView = () => {
	const intl = useIntl();
	const menuClasses = useInteractionMenuStyle();
	const classes = useStyle();
	const appStoreActions = useAppCacheStore((state: AppCacheActions) => state);
	const supervisorAgentsFilterActions = useSupervisorAgentsFilterStore(
		(actions: SupervisorAgentsFilterActions) => actions
	);

	const supervisorAgentsFilterState = useSupervisorAgentsFilterStore(
		(state: SupervisorAgentsFilterState) => state.filterData
	);

	useEffect(() => {
		appStoreActions.setCache(
			CacheKey.SupervisorAgentsFilterData,
			supervisorAgentsFilterState
		);
	}, [supervisorAgentsFilterState]);

	const cleanFilters = () => {
		let filterInitialValues = getInitialSupervisorFilterData();
		filterInitialValues.searchText = supervisorAgentsFilterState.searchText;
		filterInitialValues.queueIds = supervisorAgentsFilterState.queueIds;

		supervisorAgentsFilterActions.setFilterData(filterInitialValues);

		setAvailabilityCheckedValues({
			availability: [],
		});

		setDesireCheckedValues({
			desire: [],
		});
	};

	const menuGroupHeaderItem = useCallback(
		(filterMessage: string) => [
			<MenuGroupHeader
				key="interactionHeader"
				className={menuClasses.filterGroupHeader}
			>
				{filterMessage}
				<Button
					appearance="transparent"
					className={menuClasses.cleanButtonSize}
					onClick={() => {
						cleanFilters();
					}}
				>
					{intl.formatMessage({ id: "InteractionMenu.Reset" })}
				</Button>
			</MenuGroupHeader>,
			<MenuDivider key="interactionDivider" />,
		],
		[]
	);

	const isOnlineChange = useCallback(
		(isOnline: boolean | null) => {
			supervisorAgentsFilterActions.setIsOnline(isOnline);
		},
		[supervisorAgentsFilterActions]
	);

	const hasClientChange = useCallback(
		(hasClient: boolean | null) => {
			supervisorAgentsFilterActions.setHasClient(hasClient);
		},
		[supervisorAgentsFilterActions]
	);

	const isJoinedChange = useCallback(
		(isJoined: boolean | null) => {
			supervisorAgentsFilterActions.setIsJoined(isJoined);
		},
		[supervisorAgentsFilterActions]
	);

	const getAvailabilitySetObject = () => {
		return {
			availability:
				supervisorAgentsFilterState?.presences?.map((x) => x.toString()) ?? [],
		} as Record<string, string[]>;
	};

	const getDesireSetObject = () => {
		return {
			desire:
				supervisorAgentsFilterState?.desire?.map((x) => x.toString()) ?? [],
		} as Record<string, string[]>;
	};

	const [availabilityCheckedValues, setAvailabilityCheckedValues] = useState<
		Record<string, string[]>
	>(getAvailabilitySetObject());

	const [desireCheckedValues, setDesireCheckedValues] =
		useState<Record<string, string[]>>(getDesireSetObject());

	const onAvailabilityChange: MenuProps["onCheckedValueChange"] = (
		_,
		{ name, checkedItems }
	) => {
		setAvailabilityCheckedValues((s) => {
			return { ...(s ?? {}), [name]: checkedItems };
		});
	};

	const onDesireChange: MenuProps["onCheckedValueChange"] = (
		_,
		{ name, checkedItems }
	) => {
		setDesireCheckedValues((s) => {
			return { ...(s ?? {}), [name]: checkedItems };
		});
	};

	useEffect(() => {
		if (availabilityCheckedValues["availability"]) {
			supervisorAgentsFilterActions.setPresences(
				availabilityCheckedValues["availability"]?.map(
					(x) => x as Availabilities
				)
			);
		}
	}, [availabilityCheckedValues]);

	useEffect(() => {
		if (desireCheckedValues["desire"]) {
			supervisorAgentsFilterActions.setDesire(
				desireCheckedValues["desire"]?.map((x) => Number(x)) || []
			);
		}
	}, [desireCheckedValues]);

	return (
		<Flex column className={classes.generalFilterContainer}>
			{menuGroupHeaderItem(
				intl.formatMessage({ id: "InteractionMenu.ShowOnly" })
			)}
			<Text className={classes.filterSection}>
				{intl.formatMessage({ id: "Supervisor.Filters.General" })}
			</Text>
			<RadioGroup
				value={supervisorAgentsFilterState?.isOnline?.toString() ?? "null"}
				onChange={(_, data) => isOnlineChange(castStringToBoolean(data.value))}
				layout="horizontal"
				className={classes.filterRadioGroup}
			>
				<Radio
					value="true"
					label={intl.formatMessage({
						id: "Supervisor.Filters.Online",
					})}
					className={classes.radioElementBig}
				/>
				<Radio
					value="false"
					label={intl.formatMessage({ id: "Supervisor.Filters.Offline" })}
					className={classes.radioElementBig}
				/>
				<Radio
					value="null"
					label={intl.formatMessage({ id: "Supervisor.Filters.All" })}
					className={classes.radioElementSmall}
				/>
			</RadioGroup>
			<Divider />
			<RadioGroup
				value={supervisorAgentsFilterState?.hasClient?.toString() ?? "null"}
				onChange={(_, data) => hasClientChange(castStringToBoolean(data.value))}
				layout="horizontal"
				className={classes.filterRadioGroup}
			>
				<Radio
					value="true"
					label={intl.formatMessage({ id: "Supervisor.Filters.HasClient" })}
					className={classes.radioElementBig}
				/>
				<Radio
					value="false"
					label={intl.formatMessage({ id: "Supervisor.Filters.Doesn't" })}
					className={classes.radioElementBig}
				/>
				<Radio
					value="null"
					label={intl.formatMessage({ id: "Supervisor.Filters.All" })}
					className={classes.radioElementSmall}
				/>
			</RadioGroup>
			<Divider />
			<RadioGroup
				value={supervisorAgentsFilterState?.isJoined?.toString() ?? "null"}
				onChange={(_, data) => isJoinedChange(castStringToBoolean(data.value))}
				layout="horizontal"
				className={classes.filterRadioGroup}
			>
				<Radio
					value="true"
					label={intl.formatMessage({ id: "Supervisor.Filters.Joined" })}
					className={classes.radioElementBig}
				/>
				<Radio
					value="false"
					label={intl.formatMessage({ id: "Supervisor.Filters.NotJoined" })}
					className={classes.radioElementBig}
				/>
				<Radio
					value="null"
					label={intl.formatMessage({ id: "Supervisor.Filters.All" })}
					className={classes.radioElementSmall}
				/>
			</RadioGroup>
			<Divider />
			<Text className={classes.filterSection}>
				{intl.formatMessage({ id: "Supervisor.Filters.Status" })}
			</Text>
			<MenuList
				checkedValues={availabilityCheckedValues}
				onCheckedValueChange={onAvailabilityChange}
				className={classes.presenceMenu}
			>
				{initialSupervisorFilterAvailabilityList.map((availability) => {
					return (
						<MenuItemCheckbox
							icon={
								<PresenceBadge
									size="small"
									status={mapPresenceToStatus(availability)}
								/>
							}
							name="availability"
							value={availability.toString()}
							className={mergeClasses(
								menuClasses.filterCheckboxItem,
								classes.presenceElement
							)}
						>
							{translateActivity(availability, intl)}
						</MenuItemCheckbox>
					);
				})}
			</MenuList>
			<Divider />
			<Text className={classes.filterSection}>
				{intl.formatMessage({ id: "Supervisor.Filters.Desire" })}
			</Text>
			<MenuList
				checkedValues={desireCheckedValues}
				onCheckedValueChange={onDesireChange}
				className={classes.presenceMenu}
			>
				{desireTypes.map((type) => {
					return (
						<MenuItemCheckbox
							key={type}
							icon={<Circle16Filled color={desireColors[type]} />}
							name="desire"
							value={type.toString()}
							className={mergeClasses(
								menuClasses.filterCheckboxItem,
								classes.presenceElement
							)}
						>
							{desireName(type, intl)}
						</MenuItemCheckbox>
					);
				})}
			</MenuList>
		</Flex>
	);
};
